import { EntityType } from '@frontend/common';
import { Application, Feature, Functionality, FunctionalityType } from '@frontend/functionality/types';
import { filterPlatformFunctionalities } from '@frontend/functionality/utils';
import { useLicenseRepository } from '@frontend/repository';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { TransactionClient } from '@frontend/transaction/api';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { type Dispatch, useEffect, useState } from 'react';

import { useScreen } from '../useScreen';

export type NivalizTransactionMethod = 'coffin' | 'urn' | 'other';
export type TransactionMethodConfigurationError = 'functionality' | 'workflow' | null;

interface NivalizTransactionConfiguration {
    feature: Feature;
    functionality: Functionality;
    workflowId: string;
}

export interface TransactionMethodViewProps {
    functionality: Functionality | null;
    application: Application | null;
    feature: Feature | null;
    isNivaliz: boolean;
    selectedMethod: NivalizTransactionMethod | null;
    setSelectedMethod: Dispatch<NivalizTransactionMethod>;
    isResolvingConfiguration: boolean;
    configurationError: TransactionMethodConfigurationError;
    submitDisabled: boolean;
    onSubmit: () => Promise<void>;
}

export const featureByMethod: Record<NivalizTransactionMethod, Feature> = {
    coffin: Feature.INBOUND_TRANSACTION,
    urn: Feature.OUTBOUND_TRANSACTION,
    other: Feature.TRANSACTIONS
};

export const useTransactionMethod = (props: { wizard_id: string }): TransactionMethodViewProps => {
    const { store, updateWizardData, onSubmit: onContinue } = useScreen(props.wizard_id);
    const { functionalities } = useLicenseRepository();
    const functionality = store?.functionality ?? null;
    const application = functionality?.application ?? null;
    const feature = functionality?.feature ?? null;
    const isNivaliz = application === Application.NIVALIZ;
    const [selectedMethod, setSelectedMethodState] = useState<NivalizTransactionMethod | null>(store?.data.nivalizTransactionMethod ?? null);
    const [configuration, setConfiguration] = useState<NivalizTransactionConfiguration | null>(null);
    const [configurationError, setConfigurationError] = useState<TransactionMethodConfigurationError>(null);
    const [isResolvingConfiguration, setIsResolvingConfiguration] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isNivaliz || !selectedMethod || !functionality) {
            setConfiguration(null);
            setConfigurationError(null);
            setIsResolvingConfiguration(false);
            return;
        }

        let cancelled = false;
        const selectedFeature = featureByMethod[selectedMethod];
        const selectedFunctionalities = filterPlatformFunctionalities(
            functionalities,
            Application.NIVALIZ,
            selectedFeature,
            functionality.type ?? FunctionalityType.CORPORATE
        );

        setConfiguration(null);
        setConfigurationError(null);

        if (selectedFunctionalities.length === 0) {
            setConfigurationError('functionality');
            setIsResolvingConfiguration(false);
            return;
        }

        setIsResolvingConfiguration(true);
        void (async () => {
            for (const selectedFunctionality of selectedFunctionalities) {
                if (cancelled) return;

                try {
                    const response = await SettingClient.fetchSettings({
                        entity_type: EntityType.FUNCTIONALITY,
                        entity_id: selectedFunctionality.id
                    });
                    if (cancelled) return;

                    const workflowId = response.results.find((setting) => setting.key === SettingKey.TRANSACTION_WORKFLOW_ID)?.value;
                    if (typeof workflowId !== 'string' || !workflowId) continue;

                    setConfiguration({
                        feature: selectedFeature,
                        functionality: selectedFunctionality,
                        workflowId
                    });
                    return;
                } catch {
                    // Try the next matching functionality before reporting that no workflow can be resolved.
                }
            }

            if (!cancelled) setConfigurationError('workflow');
        })().finally(() => {
            if (!cancelled) setIsResolvingConfiguration(false);
        });

        return () => {
            cancelled = true;
        };
    }, [functionalities, functionality, isNivaliz, selectedMethod]);

    const setSelectedMethod = (method: NivalizTransactionMethod) => {
        setSelectedMethodState(method);
        updateWizardData({ nivalizTransactionMethod: method });
    };

    const onSubmit = async () => {
        if (!store || isSubmitting || (isNivaliz && (!selectedMethod || !configuration))) return;

        setIsSubmitting(true);
        try {
            if (isNivaliz && selectedMethod && configuration && store.data.transaction?.id) {
                const transaction = await TransactionClient.patchTransaction(store.wizard.account_id, store.data.transaction.id, {
                    feature: configuration.feature,
                    workflow_id: configuration.workflowId
                });
                updateWizardData({
                    nivalizTransactionMethod: selectedMethod,
                    transactionFunctionality: configuration.functionality,
                    transaction
                });
            }

            const trigger = isNivaliz && selectedMethod === 'coffin' ? ScreenTrigger.SUBMIT_FALSE : ScreenTrigger.SUBMIT;
            const nextStep = store.steps.find((step) => step.screen === ScreenType.TRANSACTION_METHOD && step.trigger === trigger);
            await onContinue(nextStep);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        functionality,
        application,
        feature,
        isNivaliz,
        selectedMethod,
        setSelectedMethod,
        isResolvingConfiguration,
        configurationError,
        submitDisabled:
            isSubmitting ||
            isResolvingConfiguration ||
            (isNivaliz && (!selectedMethod || !configuration || !!configurationError || !store?.data.transaction?.id)),
        onSubmit
    };
};
